/**
 * Enhanced Security Gate Component - Comprehensive Access Control
 * Prevents unauthorized access, mobile bypasses, and authentication bypasses
 * Protects proprietary algorithms and sensitive business logic
 */

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from './AuthContext';

const SecurityGate = ({ children, requireDesktop = false, requireMobile = false, requireAuth = true }) => {
  const navigate = useNavigate();
  const { isAuthenticated, loading, user } = useAuth();
  const [securityCheck, setSecurityCheck] = useState('checking');

  useEffect(() => {
    const performSecurityCheck = () => {
      // Security Check 1: Authentication requirement
      if (requireAuth && !loading && !isAuthenticated) {
        setSecurityCheck('auth_required');
        return;
      }

      // Security Check 2: Device type validation
      const userAgent = navigator.userAgent;

      // Detect iPad (including when requesting desktop site)
      // iPads should be treated as desktop devices due to large screen size
      const isIPad = /iPad/i.test(userAgent) ||
                     (userAgent.includes('Macintosh') && navigator.maxTouchPoints > 1);

      // iPad devices should be excluded from both viewport AND user agent checks
      const isMobileViewport = !isIPad && window.innerWidth <= 768;
      const isMobileUserAgent = !isIPad && /Android|webOS|iPhone|iPod|BlackBerry|IEMobile|Opera Mini|Mobile|mobile|CriOS|FxiOS|EdgiOS/i.test(userAgent);

      const isMobile = isMobileViewport || isMobileUserAgent;

      // SECURITY: Block mobile users from accessing desktop-only components (CRITICAL)
      if (requireDesktop && isMobile) {
        setSecurityCheck('desktop_required');
        return;
      }

      // SECURITY: Block desktop users from mobile-specific routes
      if (requireMobile && !isMobile) {
        setSecurityCheck('mobile_required');
        return;
      }

      // Security Check 3: User session validation
      if (requireAuth && isAuthenticated) {
        if (!user || !user.id) {
          setSecurityCheck('session_invalid');
          return;
        }

        // Check email verification for sensitive operations
        if (user.is_email_verified === false) {
          setSecurityCheck('email_verification_required');
          return;
        }
      }

      // Security Check 4: User Agent manipulation detection
      // userAgent already declared above in Security Check 2
      const hasSuspiciousUA = (
        userAgent.includes('Desktop') && userAgent.includes('Mobile')
      ) || (
        userAgent.length < 20 || userAgent.length > 1000
      ) || (
        /bot|crawler|spider|scraper/i.test(userAgent)
      );

      if (hasSuspiciousUA) {
        if (requireDesktop) {
          setSecurityCheck('suspicious_ua');
          return;
        }
      }

      // Security Check 5: Origin validation (anti-CSRF)
      const currentOrigin = window.location.origin;
      const allowedOrigins = [
        'https://knotcord.com',
        'https://www.knotcord.com',
        'http://localhost:3000',
        'http://127.0.0.1:3000'
      ];

      if (!allowedOrigins.includes(currentOrigin) &&
          !currentOrigin.match(/^http:\/\/192\.168\.\d+\.\d+:3000$/)) {
        setSecurityCheck('invalid_origin');
        return;
      }

      // All security checks passed
      setSecurityCheck('passed');
    };

    // Only run security checks after auth loading is complete
    if (loading) {
      setSecurityCheck('checking');
    } else {
      performSecurityCheck();
    }

    // Re-check on window resize to prevent viewport manipulation
    const handleResize = () => {
      setTimeout(performSecurityCheck, 100);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [navigate, requireDesktop, requireMobile, requireAuth, isAuthenticated, loading, user]);

  // Render security state UI
  // Only show loading screen if we're checking AND user is not authenticated yet
  // If user is authenticated, skip the loading screen to avoid flash
  if ((securityCheck === 'checking' || loading) && !isAuthenticated) {
    return (
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100vw',
        height: '100vh',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        background: '#f5f5f5',
        fontFamily: "'Menlo', 'Consolas', 'Monaco', 'Courier New', monospace",
        fontSize: '1.2rem',
        color: '#666'
      }}>
        Loading...
      </div>
    );
  }

  // If still checking but user IS authenticated, show nothing (will render children quickly)
  if (securityCheck === 'checking' || loading) {
    return null;
  }

  if (securityCheck === 'auth_required') {
    // Redirect to auth page with return URL
    const currentPath = window.location.pathname; // Get current path from BrowserRouter
    navigate(`/auth?redirect=${encodeURIComponent(currentPath)}`, { replace: true });
    return null;
  }

  if (securityCheck === 'desktop_required' || securityCheck === 'suspicious_ua') {
    window.location.replace('/mobile-home.html');
    return null;
  }

  if (securityCheck === 'mobile_required') {
    navigate('/', { replace: true });
    return null;
  }

  if (securityCheck === 'session_invalid') {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        background: '#000',
        color: '#ff6b6b',
        flexDirection: 'column'
      }}>
        <h2>⚠️ Session Error</h2>
        <p>Your session appears to be corrupted. Please sign in again.</p>
        <button 
          onClick={() => {
            localStorage.clear();
            sessionStorage.clear();
            window.location.reload();
          }}
          style={{
            marginTop: '1rem',
            padding: '0.7rem 1.5rem',
            background: '#ff6b6b',
            color: '#fff',
            border: 'none',
            borderRadius: '6px',
            cursor: 'pointer',
            fontSize: '1rem'
          }}
        >
          Clear Session & Reload
        </button>
      </div>
    );
  }

  if (securityCheck === 'email_verification_required') {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        background: '#000',
        color: '#ffa500',
        flexDirection: 'column'
      }}>
        <h2>📧 Email Verification Required</h2>
        <p>Please verify your email address to access advanced features.</p>
        <p>Check your inbox for a verification email.</p>
      </div>
    );
  }

  if (securityCheck === 'invalid_origin') {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        background: '#000',
        color: '#ff6b6b',
        flexDirection: 'column'
      }}>
        <h2>🚨 Security Error</h2>
        <p>Invalid access origin detected.</p>
        <p>Please access the application through the official website.</p>
      </div>
    );
  }

  // All security checks passed - render protected content
  return <>{children}</>;
};

export default SecurityGate;